import { useState } from 'react'
import { Dialog } from '@headlessui/react'
import {
  X,
  CheckCircle2,
  XCircle,
  HelpCircle,
  AlertCircle,
  Truck,
  DollarSign,
  PackageX,
  MessageSquare,
  UserX,
  FileText,
} from 'lucide-react'

export interface CloseConversationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (payload: { closeReason: string; closeNotes?: string }) => void
  isLoading?: boolean
  contactName?: string
  defaultIsWon?: boolean
}

type CloseCategory = 'won' | 'lost'

interface ReasonOption {
  code: string
  label: string
  description: string
  icon: typeof CheckCircle2
}

const WON_REASONS: ReasonOption[] = [
  {
    code: 'SALE_CONFIRMED',
    label: 'Pedido Confirmado',
    description: 'La orden fue confirmada por el bot o el asesor comercial.',
    icon: CheckCircle2,
  },
  {
    code: 'PAYMENT_RECEIVED',
    label: 'Pago Verificado',
    description: 'El cliente realizó el pago y el comprobante fue validado.',
    icon: DollarSign,
  },
  {
    code: 'ORDER_DELIVERED',
    label: 'Pedido Entregado',
    description: 'El pedido fue despachado o retirado con éxito.',
    icon: Truck,
  },
]

const LOST_REASONS: ReasonOption[] = [
  {
    code: 'ABANDONED_CART',
    label: 'Carrito Abandonado / Sin Respuesta',
    description: 'El cliente dejó de contestar durante el proceso de compra.',
    icon: MessageSquare,
  },
  {
    code: 'OUT_OF_COVERAGE',
    label: 'Fuera de Zona de Entrega',
    description: 'La ubicación del cliente no cuenta con cobertura de delivery.',
    icon: AlertCircle,
  },
  {
    code: 'OUT_OF_STOCK',
    label: 'Sin Stock / Agotado',
    description: 'No se contaba con el producto o insumo solicitado.',
    icon: PackageX,
  },
  {
    code: 'PRICE_OBJECTION',
    label: 'Objeción de Precio / Presupuesto',
    description: 'El cliente consideró el precio muy alto o fuera de su alcance.',
    icon: DollarSign,
  },
  {
    code: 'CANCELLED_BY_CLIENT',
    label: 'Cancelado por el Cliente',
    description: 'El cliente desistió voluntariamente de la compra.',
    icon: XCircle,
  },
  {
    code: 'CUSTOMER_SUPPORT',
    label: 'Consulta de Soporte / Informativa',
    description: 'Solo solicitaba información general, horarios o soporte post-venta.',
    icon: HelpCircle,
  },
  {
    code: 'SPAM_OR_WRONG_NUMBER',
    label: 'Número Equivocado o Spam',
    description: 'Contacto no válido o mensaje no comercial.',
    icon: UserX,
  },
]

export function CloseConversationModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  contactName,
  defaultIsWon = false,
}: CloseConversationModalProps) {
  const [category, setCategory] = useState<CloseCategory>(defaultIsWon ? 'won' : 'lost')
  const [selectedReason, setSelectedReason] = useState<string>(
    defaultIsWon ? 'SALE_CONFIRMED' : 'ABANDONED_CART'
  )
  const [notes, setNotes] = useState<string>('')

  if (!isOpen) return null

  const handleCategoryChange = (newCat: CloseCategory) => {
    setCategory(newCat)
    if (newCat === 'won') {
      setSelectedReason('SALE_CONFIRMED')
    } else {
      setSelectedReason('ABANDONED_CART')
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onConfirm({
      closeReason: selectedReason,
      closeNotes: notes.trim() ? notes.trim() : undefined,
    })
  }

  const currentReasons = category === 'won' ? WON_REASONS : LOST_REASONS

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity" aria-hidden="true" />

      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all border border-slate-100">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-5 py-4">
              <div>
                <Dialog.Title className="text-base font-bold text-slate-800">
                  Cerrar Conversación
                </Dialog.Title>
                <p className="text-xs text-slate-500">
                  {contactName
                    ? `Registra el motivo de cierre para ${contactName}`
                    : 'Registra el resultado de la conversación para métricas y CRM'}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Paso 1: Venta Concluida o No Concretada */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  ¿Se concretó la venta?
                </label>
                <div className="grid grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={() => handleCategoryChange('won')}
                    className={`flex items-center justify-center gap-2 rounded-xl border p-3 text-xs font-bold transition-all ${
                      category === 'won'
                        ? 'border-emerald-500 bg-emerald-50/70 text-emerald-800 ring-2 ring-emerald-500/20 shadow-xs'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <CheckCircle2 className={`h-4 w-4 ${category === 'won' ? 'text-emerald-600' : 'text-slate-400'}`} />
                    <span>Sí, Venta Exitosa 🎉</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleCategoryChange('lost')}
                    className={`flex items-center justify-center gap-2 rounded-xl border p-3 text-xs font-bold transition-all ${
                      category === 'lost'
                        ? 'border-rose-400 bg-rose-50/70 text-rose-800 ring-2 ring-rose-400/20 shadow-xs'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <XCircle className={`h-4 w-4 ${category === 'lost' ? 'text-rose-600' : 'text-slate-400'}`} />
                    <span>No Concretada ❌</span>
                  </button>
                </div>
              </div>

              {/* Paso 2: Motivo específico */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Motivo de Cierre
                </label>
                <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
                  {currentReasons.map((opt) => {
                    const Icon = opt.icon
                    const isSelected = selectedReason === opt.code
                    return (
                      <button
                        key={opt.code}
                        type="button"
                        onClick={() => setSelectedReason(opt.code)}
                        className={`flex w-full items-start gap-2.5 rounded-xl border p-2.5 text-left transition-all ${
                          isSelected
                            ? category === 'won'
                              ? 'border-emerald-500 bg-emerald-50/50 ring-1 ring-emerald-500/30'
                              : 'border-rose-400 bg-rose-50/50 ring-1 ring-rose-400/30'
                            : 'border-slate-200/80 bg-white hover:bg-slate-50/80'
                        }`}
                      >
                        <div className="mt-0.5 shrink-0">
                          <Icon
                            className={`h-4 w-4 ${
                              isSelected
                                ? category === 'won'
                                  ? 'text-emerald-700'
                                  : 'text-rose-600'
                                : 'text-slate-400'
                            }`}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-slate-900 leading-tight">
                            {opt.label}
                          </p>
                          <p className="text-[11px] text-slate-500 line-clamp-1 mt-0.5">
                            {opt.description}
                          </p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Paso 3: Notas Adicionales */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                  <span className="flex items-center gap-1">
                    <FileText className="h-3 w-3" />
                    <span>Detalles o Notas Internas (Opcional)</span>
                  </span>
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ej: Cliente pidió pagar en efectivo pero vive fuera de zona de reparto..."
                  rows={2}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-800 placeholder-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>

              {/* Acciones */}
              <div className="mt-5 flex items-center justify-end gap-2.5 border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isLoading}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-xs font-bold text-white shadow-xs hover:bg-slate-800 transition-colors disabled:opacity-50"
                >
                  {isLoading && <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />}
                  <span>Confirmar y Cerrar</span>
                </button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  )
}
